import { useMachineSettingsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineSettingsDialogState';
import { HistoryTab, MachineSettingsQueryParams } from 'enums/machine-settings';
import {
  IMachineSettingsDateFilter,
  IMachineSettingsURLParams,
} from 'interfaces/i-machine-settings';
import {
  IShotHistoryTableFilters,
  ShotHistoryTableConfidence,
  ShotHistoryTableMode,
  ShotHistoryTableStaticValues,
} from 'interfaces/i-machine-shots-history-table';

export const useMachineShotHistoryDialogState = () => {
  const { getParams, setParams } = useMachineSettingsDialogState();

  const searchParams = getParams();

  // e.g. &shotID=123abc&shotMode=training&shotPitchID=456def&shotDateStart=2023-01-01&shotDateEnd=2023-01-31&shotConfidence=1&shotHasVideo=true
  const shotID = searchParams.get(MachineSettingsQueryParams.ShotID);

  const shotMode = searchParams.get(MachineSettingsQueryParams.ShotMode);
  const shotPitchName = searchParams.get(
    MachineSettingsQueryParams.ShotPitchName
  );

  const shotHitterID = searchParams.get(
    MachineSettingsQueryParams.ShotHitterID
  );

  const shotDateEnd = searchParams.get(MachineSettingsQueryParams.ShotDateEnd);
  const shotDateStart = searchParams.get(
    MachineSettingsQueryParams.ShotDateStart
  );

  const shotConfidence = searchParams.get(
    MachineSettingsQueryParams.ShotConfidence
  );

  const shotHasVideo = searchParams.get(
    MachineSettingsQueryParams.ShotHasVideo
  );

  const historyTab = searchParams.get(MachineSettingsQueryParams.HistoryTab);

  const shotFilters: IShotHistoryTableFilters = {
    mode: (shotMode as ShotHistoryTableMode) || '',
    confidence: (shotConfidence as ShotHistoryTableConfidence) || '',
    hasVideo: (shotHasVideo as ShotHistoryTableStaticValues) || '',
    pitchName: shotPitchName || '',
    hitterID: shotHitterID || '',
    dateRange:
      shotDateStart && shotDateEnd
        ? {
            start: new Date(shotDateStart),
            end: new Date(shotDateEnd),
          }
        : undefined,
  };

  const setShotID = (id: string) => {
    setParams(
      {
        [MachineSettingsQueryParams.ShotID]: id,
      },
      'setShotID'
    );
  };

  const resetShotID = () => {
    setParams(
      {
        [MachineSettingsQueryParams.ShotID]: undefined,
      },
      'resetShotID'
    );
  };

  const setShotFilter = (v: Partial<IShotHistoryTableFilters>) => {
    const updated: Partial<IMachineSettingsURLParams> = {};

    for (const k in v) {
      const key = k as keyof IShotHistoryTableFilters;
      const value = v[key];

      switch (key) {
        case 'dateRange': {
          if (!value) {
            updated[MachineSettingsQueryParams.ShotDateStart] = undefined;
            updated[MachineSettingsQueryParams.ShotDateEnd] = undefined;
            break;
          }

          const dateRange = value as IMachineSettingsDateFilter;

          updated[MachineSettingsQueryParams.ShotDateStart] =
            dateRange.start.toISOString();
          updated[MachineSettingsQueryParams.ShotDateEnd] =
            dateRange.end.toISOString();
          break;
        }

        case 'mode':
          if (!value) {
            updated[MachineSettingsQueryParams.ShotMode] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ShotMode] =
            value as ShotHistoryTableMode;
          break;

        case 'confidence':
          if (value === undefined) {
            updated[MachineSettingsQueryParams.ShotConfidence] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ShotConfidence] =
            value as ShotHistoryTableConfidence;
          break;

        case 'hasVideo':
          if (value === undefined) {
            updated[MachineSettingsQueryParams.ShotHasVideo] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ShotHasVideo] =
            value as ShotHistoryTableStaticValues;
          break;

        case 'pitchName':
          if (!value) {
            updated[MachineSettingsQueryParams.ShotPitchName] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ShotPitchName] = value as string;
          break;

        case 'hitterID':
          if (!value) {
            updated[MachineSettingsQueryParams.ShotHitterID] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ShotHitterID] = value as string;
          break;
        default:
          break;
      }
    }

    setParams(updated, 'setShotFilter');
  };

  const setHistoryTab = (tab: HistoryTab) => {
    setParams(
      {
        [MachineSettingsQueryParams.HistoryTab]: tab,
      },
      'setHistoryTab'
    );
  };

  return {
    shotID,
    setShotID,
    resetShotID,
    shotFilters,
    setShotFilter,
    historyTab,
    setHistoryTab,
  };
};
